import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';
import { Order, TableDataType } from 'app/agriculture/components/Table/SortableTable';
import './SortableTableHead.scss';

export interface HeadCell<DataType> {
    // the property of DataType we want to display the data of
    key: keyof DataType;
    // the label to display for this column
    label: string;
    align?: 'left' | 'right' | 'center';
    // should we hide this column?
    isHidden?: boolean;
}

interface SortableTableHeadProps<DataType> {
    headCells: HeadCell<DataType>[];
    hasButton: boolean;
    onRequestSort: (property: keyof DataType) => void;
    order: Order;
    orderBy: string | number | symbol | null;
    rowCount: number;
    disableSort?: boolean;
}

export function SortableTableHead<DataType extends TableDataType>({
    order,
    orderBy,
    onRequestSort,
    headCells,
    hasButton = false,
    disableSort = false,
}: SortableTableHeadProps<DataType>) {
    const createSortHandler = (headCell: HeadCell<DataType>) => () => {
        onRequestSort(headCell.key);
    };

    return (
        <TableHead className="SortableTableHead">
            <TableRow>
                {headCells
                    .filter((cell) => cell.isHidden !== true)
                    .map((headCell, index) => (
                        <TableCell
                            key={headCell.key.toString()}
                            align="left"
                            padding={index == 0 ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.key ? order : false}
                            data-testid={`table-head-cell-${index}`}
                        >
                            <TableSortLabel
                                active={!disableSort && orderBy === headCell.key}
                                direction={orderBy === headCell.key ? order : 'asc'}
                                onClick={createSortHandler(headCell)}
                                data-testid={`table-head-cell-sort-button-${index}`}
                            >
                                <label className="SortableTableHead__label">{headCell.label}</label>
                                {!disableSort && orderBy === headCell.key ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc'
                                            ? 'sorted descending'
                                            : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                {hasButton ? <TableCell /> : null}
            </TableRow>
        </TableHead>
    );
}
